/**
 * Copyright (c) 2022 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React, { useContext, useState } from 'react';
import { Form } from 'react-final-form';
import arrayMutators from 'final-form-arrays';

import { Button } from '@blend/alchemy';

import { AlertBar, Modal } from 'blb-components/GrommetComponents';
import { useToasts } from 'blb-components/AuthenticatedApp/Toast';
import { addTeamMembers } from 'blb-api/groups';

import { BlendBridge } from 'blb-types/blendBridge';
import { InviteTeamMembers, MemberAssignment, MembersFormValues } from './CreateTeamModal/InviteTeamMembers';
import { getPersona } from 'blb-persona/index';
import { MeContext } from 'blb-contexts/Me';

const ModalStyles = {
  content: {
    height: 'fit-content',
    width: '500px',
  },
};

const convertMembersDataForAPI = (members: MemberAssignment[]) =>
  members.map(member => {
    const { isAdmin, ...memberDetails } = member;

    return {
      ...memberDetails,
      role: isAdmin ? BlendBridge.API.GroupRoles.ADMIN : BlendBridge.API.GroupRoles.MEMBER,
    };
  });

interface InviteTeamMembersModalProps {
  handleClose: (shouldRefetch?: boolean) => Promise<void>;
  team: BlendBridge.API.SearchGroupRecord;
}

const InviteTeamMembersModal: React.FC<InviteTeamMembersModalProps> = ({ handleClose, team }) => {
  const [error, setError] = useState<Error | undefined>();
  const me = useContext(MeContext)!;

  const Toaster = useToasts()!;

  const handleCloseModal = async () => {
    setError(undefined);
    await handleClose(true);
  };

  const onSubmit = async (formValues: MembersFormValues) => {
    setError(undefined);

    try {
      const { members } = formValues;

      const memberAssignments = convertMembersDataForAPI(members);
      await addTeamMembers(team.id, { assignments: memberAssignments });

      Toaster.addToast({
        message: `Team members successfully invited for ${team.name}.`,
        theme: 'info',
      });
      await handleCloseModal();
    } catch (err) {
      setError(err as Error);
    }
  };

  return (
    <Form
      mutators={{
        ...arrayMutators,
      }}
      onSubmit={onSubmit}
      render={({ form, handleSubmit, submitting }) => {
        return (
          <Modal
            buttonComponents={
              <Button
                disabled={submitting}
                id="it-invite-team-members-submit"
                label={'Invite members'}
                onClick={handleSubmit}
                primary
              />
            }
            handleClose={handleCloseModal}
            id="invite-team-members-modal"
            isOpen
            style={ModalStyles}
            title={'Invite team members'}
          >
            {error ? (
              <AlertBar
                boxProps={{ margin: { bottom: 'medium' } }}
                errorMessage={`Error while trying to invite members to team. Ensure they are not already in the team or pending an invitation response. Otherwise please try again or contact support at ${
                  getPersona(me.principalType).getSupportContactInfo().email
                }`}
                id="it-create-group-error"
              />
            ) : null}
            <InviteTeamMembers form={form} />
          </Modal>
        );
      }}
    ></Form>
  );
};

export default InviteTeamMembersModal;
